import React, { useState, useEffect, useMemo } from "react";
import {
  Paper,
  Typography,
  TextField,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Button,
  CircularProgress,
  Tooltip,
  Badge,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from "@mui/material";
import SettingsIcon from '@mui/icons-material/Settings';

function AgentsList({ agents, onSelectAgent, onCreateNew, selectedAgentId }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");

  // Proyectos disponibles (memoizado)
  const availableProjects = useMemo(() => {
    if (!agents || agents.length === 0) return [];
    const projectSet = new Set();
    agents.forEach(agent => {
      projectSet.add(agent.project || "default");
    });
    return Array.from(projectSet).sort();
  }, [agents]); // Solo recalcula si 'agents' cambia

  // Efecto para resetear el filtro si el proyecto seleccionado desaparece
  useEffect(() => {
    if (projectFilter !== "all" && !availableProjects.includes(projectFilter)) {
      setProjectFilter("all");
    }
    // Si no hay proyectos disponibles y el filtro no es 'all', resetear
    if (availableProjects.length === 0 && projectFilter !== "all") {
       setProjectFilter("all");
    }
  }, [availableProjects, projectFilter]);

  // Agentes filtrados (memoizado)
  const filteredAgents = useMemo(() => {
    return agents.filter((agent) => {
      // Filtrar por proyecto
      const agentProject = agent.project || "default";
      const matchesProject = projectFilter === "all" || agentProject === projectFilter;
      if (!matchesProject) return false;

      // Filtrar por texto
      return agent.agent_id.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [agents, projectFilter, searchQuery]); // Recalcula si cambia alguna dependencia

  return (
    <Paper
      sx={{
        width: 300,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        height: 'calc(100vh - 80px)', // Ajustar altura para ocupar espacio vertical (ejemplo)
        overflow: 'hidden' // Evitar que el paper crezca más allá de la altura
      }}
    >
      <Typography variant="h6" sx={{ p: 2, flexShrink: 0 }}> {/* Evitar que título se encoja */}
        Lista de Agentes
      </Typography>

      {/* Contenedor de Filtros */}
      <Box sx={{ px: 2, mb: 1, flexShrink: 0 }}>
        {/* Filtro Proyecto */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="project-filter-label">Proyecto</InputLabel>
          <Select
            labelId="project-filter-label"
            id="project-filter"
            value={projectFilter}
            label="Proyecto"
            onChange={(e) => setProjectFilter(e.target.value)}
          >
            <MenuItem value="all">Todos los proyectos</MenuItem>
            {availableProjects.map((project) => (
              <MenuItem key={project} value={project}>
                {project === 'default' ? 'Default' : project}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Filtro Búsqueda */}
        <TextField
          label="Buscar agente en proyecto(s)"
          variant="outlined"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Box>

      {/* Lista (Scrollable Box) - Hacer que ocupe el espacio restante */}
      <Box
        sx={{
          flexGrow: 1, // Permitir que crezca para ocupar espacio
          overflowY: "auto", // Scroll solo vertical
          // Quitar maxHeight para que flexGrow funcione correctamente
          // maxHeight: "calc(100vh - 250px)"
        }}
      >
        <List sx={{ pt: 0 }}> {/* Quitar padding top de la lista si se desea */}
          {filteredAgents.map((agent) => (
            <ListItemButton
              key={agent.id || agent.agent_id}
              onClick={() => onSelectAgent(agent)}
              // Añadir highlight si está seleccionado
              selected={selectedAgentId === (agent.id || agent.agent_id)}
              // Aplicar estilos directamente o con sx
              // sx={selectedAgentId === (agent.id || agent.agent_id) ? { backgroundColor: 'action.selected', '&:hover': { backgroundColor: 'action.hover' } } : {}}
            >
              <ListItemText
                primary={agent.agent_id}
                secondary={
                  <>
                    {agent.description}
                    <Typography 
                      variant="caption" 
                      display="block" 
                      color="primary.main" 
                      sx={{ mt: 0.5, fontWeight: 'medium' }}
                    >
                      Proyecto: {agent.project || 'Default'}
                    </Typography>
                  </>
                }
              />
              {/* Badge MCP */}
              {agent.mcp_config && (
                <Tooltip title="Tiene configuración MCP">
                  <Badge color="primary" variant="dot" sx={{ mr: 1 }}> {/* Añadir margen si es necesario */}
                    <SettingsIcon fontSize="small" />
                  </Badge>
                </Tooltip>
              )}
            </ListItemButton>
          ))}
        </List>
      </Box>

      {/* Botón Crear Nuevo - Fijar abajo */}
      <Box sx={{ textAlign: "center", p: 2, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Button variant="contained" color="secondary" onClick={onCreateNew}>
          Crear Nuevo
        </Button>
      </Box>
    </Paper>
  );
}

export default AgentsList;
